import type {Action, AppDispatch, GetState, RootState} from "@client/store";
import {_Types} from "@reactivated";

export type Notification = _Types["Context"]["messages"][number] & {
    id: number;
    permanent?: boolean;
};

const NOTIFICATION_DURATION = 4500;

export type Actions =
    | {
          type: "CREATE_NOTIFICATION";
          notification: Notification;
      }
    | {
          type: "SET_INITIAL_NOTIFICATIONS";
          notifications: Notification[];
      }
    | {
          type: "REMOVE_NOTIFICATION";
          id: number;
      };

export const initialState = [] as Notification[];

export const reducer = (state = initialState, action: Action): typeof initialState => {
    switch (action.type) {
        case "CREATE_NOTIFICATION": {
            return [...state, action.notification];
        }
        case "SET_INITIAL_NOTIFICATIONS": {
            return [...state, ...action.notifications];
        }
        case "REMOVE_NOTIFICATION": {
            return state.filter((notification) => notification.id !== action.id);
        }
        default:
            return state;
    }
};

const notificationDismissals: {[id: number]: (() => void) | null} = {};

export const dismissNotification =
    (notification: {id: number}) => (dispatch: AppDispatch) => {
        dispatch({
            type: "REMOVE_NOTIFICATION",
            id: notification.id,
        });
        notificationDismissals[notification.id]?.();
    };

// Start way above the notifications the page could load with from Django Messages.
let nextNotificationId = 1000;

type Options =
    | {
          type: Notification["level_tag"];
          unique?: true;
          permanent?: true;
          onDismiss?: () => void;
      }
    | {
          type: Notification["level_tag"];
          unique?: true;
          permanent: never;
          onDismiss: never;
      };

export const notify =
    (message: string, options?: Options) =>
    (dispatch: AppDispatch, getState: GetState) => {
        const {type, unique} = options ?? {type: "success", unique: false};
        const onDismiss = options?.onDismiss ?? (() => {});

        const existing = getState().notifications.filter(
            (n) => n.level_tag === type && message === message,
        );
        const id =
            unique !== true || existing.length == 0
                ? nextNotificationId
                : existing[0].id;
        nextNotificationId++;

        notificationDismissals[id] = onDismiss;

        const dismiss = () => {
            dispatch(
                dismissNotification({
                    id,
                }),
            );
        };

        if (unique !== true || existing.length == 0) {
            dispatch({
                type: "CREATE_NOTIFICATION",
                notification: {
                    id,
                    message,
                    level: 0,
                    level_tag: type,
                    from_server: false,
                    permanent: options?.permanent === true,
                },
            });

            if (options?.permanent !== true) {
                setTimeout(dismiss, NOTIFICATION_DURATION);
            }
        }

        return dismiss;
    };

export const serverError = () => (dispatch: AppDispatch) => {
    dispatch(
        notify(
            `A server error occurred.
             We have logged it and will look into it. Please try again later.`,
            {type: "error", permanent: true},
        ),
    );
};

export const getNotifications = (state: RootState) => state.notifications;
